import { useMemo, useState } from "react";

import { Plural, Trans } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { AnimatePresence, m } from "framer-motion";
import { AlertCircle, ChevronDown, ChevronUp, TrendingUp } from "lucide-react";

// TODO: dedupe with journal-tab JournalEntryData
interface JournalEntryData {
  id: string;
  type: string;
  title: string;
  notes: string | null;
  eventDate: string;
  tags: string[];
  status: string;
  isPinned: boolean;
  metadata: Record<string, unknown> | null;
  providerId: string | null;
  createdAt: string;
  _count: { attachments: number; reminders: number };
  reminders: { id: string; remindAt: string; message: string; status: string }[];
}

/* ─── Types ─── */

interface LabValue {
  name: string;
  value: number;
  unit: string;
  referenceMin?: number;
  referenceMax?: number;
  date: string;
}

interface LabMetric {
  name: string;
  unit: string;
  referenceMin?: number;
  referenceMax?: number;
  points: { date: string; value: number }[];
}

/* ─── Helpers ─── */

function extractLabValues(entries: JournalEntryData[]): LabValue[] {
  const values: LabValue[] = [];
  for (const entry of entries) {
    if (entry.type !== "lab_result" || !entry.metadata) continue;
    const meta = entry.metadata as Record<string, unknown>;
    const vals = meta.values as
      | Array<{
          name?: string;
          value?: number;
          unit?: string;
          referenceMin?: number;
          referenceMax?: number;
        }>
      | undefined;
    if (!Array.isArray(vals)) continue;
    for (const v of vals) {
      if (v.name && typeof v.value === "number") {
        values.push({
          name: v.name,
          value: v.value,
          unit: v.unit || "",
          referenceMin: v.referenceMin,
          referenceMax: v.referenceMax,
          date: entry.eventDate,
        });
      }
    }
  }
  return values;
}

function groupByMetric(values: LabValue[]): LabMetric[] {
  const map = new Map<string, LabMetric>();
  for (const v of values) {
    let metric = map.get(v.name);
    if (!metric) {
      metric = {
        name: v.name,
        unit: v.unit,
        referenceMin: v.referenceMin,
        referenceMax: v.referenceMax,
        points: [],
      };
      map.set(v.name, metric);
    }
    // Update reference range if available
    if (v.referenceMin !== undefined && metric.referenceMin === undefined)
      metric.referenceMin = v.referenceMin;
    if (v.referenceMax !== undefined && metric.referenceMax === undefined)
      metric.referenceMax = v.referenceMax;
    metric.points.push({ date: v.date, value: v.value });
  }
  // Sort points by date
  for (const metric of map.values()) {
    metric.points.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }
  return Array.from(map.values()).filter((m) => m.points.length >= 1);
}

/* ─── SVG Chart ─── */

const CHART_W = 400;
const CHART_H = 200;
const PAD = { top: 20, right: 20, bottom: 40, left: 50 };
const PLOT_W = CHART_W - PAD.left - PAD.right;
const PLOT_H = CHART_H - PAD.top - PAD.bottom;

function LineChart({
  metric,
  hoveredIdx,
  setHoveredIdx,
}: {
  metric: LabMetric;
  hoveredIdx: number | null;
  setHoveredIdx: (i: number | null) => void;
}) {
  const { points, referenceMin, referenceMax } = metric;

  const allValues = points.map((p) => p.value);
  let yMin = Math.min(...allValues);
  let yMax = Math.max(...allValues);
  if (referenceMin !== undefined) yMin = Math.min(yMin, referenceMin);
  if (referenceMax !== undefined) yMax = Math.max(yMax, referenceMax);
  const yPadding = (yMax - yMin) * 0.15 || 1;
  yMin -= yPadding;
  yMax += yPadding;

  const xScale = (i: number) =>
    PAD.left + (points.length === 1 ? PLOT_W / 2 : (i / (points.length - 1)) * PLOT_W);
  const yScale = (v: number) => PAD.top + PLOT_H - ((v - yMin) / (yMax - yMin)) * PLOT_H;

  const polylinePoints = points.map((p, i) => `${xScale(i)},${yScale(p.value)}`).join(" ");

  // Y-axis ticks (5 ticks)
  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      className="w-full"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Reference range band */}
      {referenceMin !== undefined && referenceMax !== undefined && (
        <rect
          x={PAD.left}
          y={yScale(referenceMax)}
          width={PLOT_W}
          height={yScale(referenceMin) - yScale(referenceMax)}
          className="fill-primary/10"
          rx={2}
        />
      )}

      {/* Grid lines */}
      {yTicks.map((tick, i) => (
        <g key={i}>
          <line
            x1={PAD.left}
            y1={yScale(tick)}
            x2={PAD.left + PLOT_W}
            y2={yScale(tick)}
            className="stroke-border"
            strokeWidth={0.5}
            strokeDasharray="4,4"
          />
          <text
            x={PAD.left - 6}
            y={yScale(tick) + 3}
            textAnchor="end"
            className="fill-muted-foreground"
            fontSize={9}
          >
            {tick.toFixed(tick % 1 === 0 ? 0 : 1)}
          </text>
        </g>
      ))}

      {/* X-axis labels */}
      {points.map((p, i) => {
        const d = new Date(p.date);
        const label = `${d.getDate()}/${d.getMonth() + 1}`;
        return (
          <text
            key={i}
            x={xScale(i)}
            y={CHART_H - 5}
            textAnchor="middle"
            className="fill-muted-foreground"
            fontSize={9}
          >
            {label}
          </text>
        );
      })}

      {/* Unit label */}
      <text x={4} y={PAD.top - 6} className="fill-muted-foreground" fontSize={8}>
        {metric.unit}
      </text>

      {/* Line */}
      {points.length > 1 && (
        <polyline
          points={polylinePoints}
          fill="none"
          className="stroke-primary"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Data points */}
      {points.map((p, i) => {
        const isOutside =
          (referenceMin !== undefined && p.value < referenceMin) ||
          (referenceMax !== undefined && p.value > referenceMax);
        const isHovered = hoveredIdx === i;
        return (
          <g key={i}>
            {/* Hit area for touch */}
            <circle
              cx={xScale(i)}
              cy={yScale(p.value)}
              r={16}
              fill="transparent"
              className="cursor-pointer"
              onPointerEnter={() => setHoveredIdx(i)}
              onPointerLeave={() => setHoveredIdx(null)}
            />
            <circle
              cx={xScale(i)}
              cy={yScale(p.value)}
              r={isHovered ? 5 : 3.5}
              className={cn("transition-all", isOutside ? "fill-red-500" : "fill-primary")}
            />
            {/* Tooltip */}
            {isHovered && (
              <g>
                <rect
                  x={xScale(i) - 36}
                  y={yScale(p.value) - 28}
                  width={72}
                  height={20}
                  rx={4}
                  className="fill-foreground/90"
                />
                <text
                  x={xScale(i)}
                  y={yScale(p.value) - 15}
                  textAnchor="middle"
                  className="fill-background"
                  fontSize={10}
                  fontWeight={600}
                >
                  {p.value} {metric.unit}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* ─── Main Component ─── */

export function LabTrendsChart({ entries }: { entries: JournalEntryData[] }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const metrics = useMemo(() => {
    const values = extractLabValues(entries);
    return groupByMetric(values);
  }, [entries]);

  // Auto-select first metric
  const activeMetricName = selectedMetric ?? metrics[0]?.name ?? null;
  const activeMetric = metrics.find((m) => m.name === activeMetricName) ?? null;

  if (metrics.length === 0) return null;

  const hasEnoughData = activeMetric && activeMetric.points.length >= 2;

  return (
    <div className="border-border bg-card rounded-xl border">
      {/* Header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex min-h-[48px] w-full items-center gap-3 p-4"
      >
        <div className="flex size-8 items-center justify-center rounded-lg bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400">
          <TrendingUp className="size-4" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-foreground text-sm font-semibold">
            <Trans>Lab Trends</Trans>
          </p>
          <p className="text-muted-foreground text-xs">
            <Plural value={metrics.length} one="# metric tracked" other="# metrics tracked" />
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="text-muted-foreground size-4" />
        ) : (
          <ChevronDown className="text-muted-foreground size-4" />
        )}
      </button>

      {/* Content */}
      <AnimatePresence>
        {expanded && (
          <m.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-border space-y-3 border-t px-4 pt-3 pb-4">
              {/* Metric selector */}
              <div className="relative">
                <select
                  value={activeMetricName || ""}
                  onChange={(e) => {
                    setSelectedMetric(e.target.value);
                    setHoveredIdx(null);
                  }}
                  className="border-border bg-background text-foreground focus:ring-primary/30 focus:border-primary min-h-[44px] w-full cursor-pointer appearance-none rounded-lg border px-3 pr-8 text-sm outline-none focus:ring-2 focus:ring-inset"
                >
                  {metrics.map((m) => (
                    <option key={m.name} value={m.name}>
                      {m.name} ({m.unit})
                    </option>
                  ))}
                </select>
                <ChevronDown className="text-muted-foreground pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2" />
              </div>

              {/* Chart or not-enough-data */}
              {hasEnoughData ? (
                <div className="bg-muted/30 rounded-lg p-2">
                  <LineChart
                    metric={activeMetric}
                    hoveredIdx={hoveredIdx}
                    setHoveredIdx={setHoveredIdx}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center py-6">
                  <AlertCircle className="text-muted-foreground size-8" />
                  <p className="text-muted-foreground mt-2 text-sm">
                    <Trans>Not enough data to show a chart</Trans>
                  </p>
                </div>
              )}

              {/* Reference range legend */}
              {activeMetric &&
                activeMetric.referenceMin !== undefined &&
                activeMetric.referenceMax !== undefined && (
                  <div className="text-muted-foreground flex items-center gap-2 text-xs">
                    <span className="bg-primary/10 border-primary/20 inline-block size-3 rounded border" />
                    <Trans>
                      Reference range: {activeMetric.referenceMin}–{activeMetric.referenceMax}{" "}
                      {activeMetric.unit}
                    </Trans>
                  </div>
                )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
